import { useState } from 'react'
import { supabase } from '@/config/supabase'
import { 
    Send, Users, MessageSquare, History, AlertCircle, CheckCircle2, 
    Smartphone, Search, BellRing, Trash2, Loader2
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

export function PushNotificationsPage() {
    const queryClient = useQueryClient()
    const [title, setTitle] = useState('')
    const [message, setMessage] = useState('')
    const [url, setUrl] = useState('/portal')
    const [target, setTarget] = useState<'all' | 'specific'>('all')
    const [sending, setSending] = useState(false)
    const [status, setStatus] = useState<{ type: 'success' | 'error', msg: string } | null>(null)

    // Busca dispositivos ativos
    const { data: tokens = [] } = useQuery({
        queryKey: ['push-tokens'],
        queryFn: async () => {
            const { data, error } = await supabase
                .schema('veicular_system')
                .from('veic_notificacao_push_tokens')
                .select('*, veic_associados(nome)')
                .order('created_at', { ascending: false })
            if (error) throw error
            return data
        }
    })

    // Busca histórico de disparos
    const { data: history = [] } = useQuery({
        queryKey: ['push-history'],
        queryFn: async () => {
            const { data, error } = await supabase
                .schema('veicular_system')
                .from('veic_notificacao_push_log')
                .select('*')
                .order('created_at', { ascending: false })
                .limit(10)
            if (error) throw error
            return data
        }
    })

    const handleSendPush = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!title || !message) return

        setSending(true)
        setStatus(null)

        try {
            // Chama a Edge Function para o disparo real
            // Vamos precisar criar essa função ou usar uma lógica via Firebase se configurado
            // Por enquanto, vamos registrar no log e simular o disparo
            const { data: { user } } = await supabase.auth.getUser()

            const { data, error } = await supabase.functions.invoke('api-push-send', {
                body: {
                    title,
                    message,
                    url,
                    target: target === 'all' ? 'broadcast' : 'specific',
                    tokens: target === 'all' ? tokens.map(t => t.token) : []
                }
            })

            if (error) throw error

            setStatus({ type: 'success', msg: `Push enviado com sucesso para ${tokens.length} dispositivos!` })
            setTitle('')
            setMessage('')
            queryClient.invalidateQueries({ queryKey: ['push-history'] })
        } catch (err: any) {
            console.error('Send Push Error:', err)
            setStatus({ type: 'error', msg: err.message || 'Erro ao enviar push. Verifique os logs.' })
        } finally {
            setSending(false)
        }
    }

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-surface-900">Notificações Push</h1>
                    <p className="text-surface-500">Gerencie e envie mensagens para os celulares dos associados</p>
                </div>
                <div className="flex items-center gap-2 bg-primary-50 text-primary-700 px-4 py-2 rounded-full border border-primary-100">
                    <Smartphone size={20} />
                    <span className="font-bold text-sm">{tokens.length} Dispositivos Online</span>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Formulário de Envio */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white rounded-2xl border border-surface-200 shadow-sm overflow-hidden transform transition-all hover:shadow-md">
                        <div className="p-6 border-b border-surface-100 bg-surface-50/50 flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary-100 text-primary-600 rounded-xl flex items-center justify-center">
                                <Send size={20} />
                            </div>
                            <h2 className="font-bold text-surface-900">Novo Disparo em Massa</h2>
                        </div>
                        
                        <form onSubmit={handleSendPush} className="p-8 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-surface-500 uppercase tracking-wider">Título da Notificação</label>
                                    <input 
                                        type="text"
                                        placeholder="Ex: Seu contrato foi renovado! 🎉"
                                        className="w-full bg-surface-50 border border-surface-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                                        value={title}
                                        onChange={e => setTitle(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-surface-500 uppercase tracking-wider">URL de Destino (Opcional)</label>
                                    <input 
                                        type="text"
                                        placeholder="/portal/faturas"
                                        className="w-full bg-surface-50 border border-surface-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                                        value={url}
                                        onChange={e => setUrl(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-surface-500 uppercase tracking-wider">Mensagem (Corpo)</label>
                                <textarea 
                                    rows={4}
                                    placeholder="Escreva a mensagem que aparecerá na tela do celular..."
                                    className="w-full bg-surface-50 border border-surface-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all resize-none"
                                    value={message}
                                    onChange={e => setMessage(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-4 border-t border-surface-100">
                                <div className="flex items-center gap-6">
                                    <label className="flex items-center gap-3 cursor-pointer group">
                                        <input 
                                            type="radio" 
                                            name="target" 
                                            className="w-5 h-5 text-primary-600 focus:ring-primary-500"
                                            checked={target === 'all'}
                                            onChange={() => setTarget('all')}
                                        />
                                        <span className="text-sm font-semibold text-surface-700 group-hover:text-primary-600">Todos os Dispositivos</span>
                                    </label>
                                    <label className="flex items-center gap-3 cursor-pointer group opacity-40 select-none">
                                        <input 
                                            type="radio" 
                                            disabled
                                            name="target" 
                                            className="w-5 h-5 text-primary-600 focus:ring-primary-500"
                                        />
                                        <span className="text-sm font-semibold text-surface-700">Segmentado (Em breve)</span>
                                    </label>
                                </div>

                                <button 
                                    type="submit"
                                    disabled={sending || tokens.length === 0}
                                    className="w-full md:w-auto bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-bold px-8 py-3 rounded-xl flex items-center justify-center gap-3 shadow-lg shadow-primary-500/20 active:scale-95 transition-all text-sm uppercase tracking-widest"
                                >
                                    {sending ? <Loader2 size={18} className="animate-spin" /> : <><Send size={18} /> Disparar Agora</>}
                                </button>
                            </div>

                            {status && (
                                <div className={`p-4 rounded-xl flex items-center gap-3 animate-in fade-in slide-in-from-top-2 ${status.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
                                    {status.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
                                    <p className="text-sm font-medium">{status.msg}</p>
                                </div>
                            )}
                        </form>
                    </div>

                    {/* Histórico */}
                    <div className="bg-white rounded-2xl border border-surface-200 shadow-sm overflow-hidden">
                        <div className="p-6 border-b border-surface-100 bg-surface-50/50 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <History size={20} className="text-surface-400" />
                                <h2 className="font-bold text-surface-900">Últimos Envios</h2>
                            </div>
                        </div>
                        <div className="divide-y divide-surface-100">
                            {history.map((log: any) => (
                                <div key={log.id} className="p-5 flex items-center justify-between hover:bg-surface-50 transition-colors">
                                    <div className="space-y-1">
                                        <p className="font-bold text-surface-900">{log.titulo}</p>
                                        <p className="text-xs text-surface-500 truncate max-w-md">{log.mensagem}</p>
                                        <div className="flex items-center gap-2 text-[10px] text-surface-400 uppercase font-bold tracking-wider pt-1">
                                            <span>{new Date(log.created_at).toLocaleString('pt-BR')}</span>
                                            <span>•</span>
                                            <span className="flex items-center gap-1"><Smartphone size={10} /> {log.total_enviados} envios</span>
                                        </div>
                                    </div>
                                    <div className="p-2 text-surface-300 hover:text-red-500 cursor-not-allowed">
                                        <Trash2 size={18} />
                                    </div>
                                </div>
                            ))}
                            {history.length === 0 && (
                                <div className="p-12 text-center text-surface-400 space-y-2">
                                    <BellRing size={40} className="mx-auto opacity-20" />
                                    <p className="text-sm font-medium">Nenhum histórico de envio encontrado</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Sidebar - Dispositivos Ativos */}
                <div className="space-y-6">
                    <div className="bg-white rounded-2xl border border-surface-200 shadow-sm overflow-hidden">
                        <div className="p-6 border-b border-surface-100 flex items-center gap-3">
                            <Users size={20} className="text-surface-400" />
                            <h2 className="font-bold text-surface-900">Dispositivos</h2>
                        </div>
                        <div className="p-4">
                            <div className="relative mb-4">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-300" size={16} />
                                <input 
                                    type="text" 
                                    placeholder="Filtrar associado..."
                                    className="w-full bg-surface-50 border border-surface-200 rounded-lg pl-10 pr-4 py-2 text-xs outline-none focus:ring-2 focus:ring-primary-500 transition-all font-medium"
                                />
                            </div>
                            <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
                                {tokens.map((token: any) => (
                                    <div key={token.id} className="flex items-center gap-3 p-3 bg-surface-50 rounded-xl border border-surface-100 group hover:border-primary-200 transition-all">
                                        <div className="w-8 h-8 bg-white border border-surface-200 rounded-lg flex items-center justify-center text-surface-400 group-hover:text-primary-600 transition-colors shadow-sm">
                                            <Smartphone size={16} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-bold text-surface-800 truncate">{token.veic_associados?.nome || 'Anônimo'}</p>
                                            <p className="text-[10px] text-surface-500 uppercase tracking-tighter">Visto em: {new Date(token.last_seen_at).toLocaleDateString('pt-BR')}</p>
                                        </div>
                                        <div className="w-2 h-2 rounded-full bg-green-500 shadow-sm shadow-green-500/50" />
                                    </div>
                                ))}
                                {tokens.length === 0 && (
                                    <p className="text-center py-8 text-xs text-surface-400 italic">Nenhum dispositivo registrado</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

